// scheme.js — M3-Farbrollen aus einer Leitfarbe (Dynamic Color), Node + Browser.
// Nutzt @material/material-color-utilities (offizielle Google-Bibliothek).
// Standard: Waldgrün als Leitfarbe, Lagerfeuer-Orange als Tertiärpalette (Akzent für
// Aktionen/Featured). Bei einer eigenen Leitfarbe leitet M3 die Tertiärfarbe selbst ab.

import {
  argbFromHex, hexFromArgb, Hct, SchemeTonalSpot, DynamicScheme, TonalPalette, MaterialDynamicColors,
} from '@material/material-color-utilities';

import { DEFAULT_SEED, DEFAULT_TERTIARY, SEED_PRESETS, CONTRAST_LEVELS } from './presets.js';
export { DEFAULT_SEED, DEFAULT_TERTIARY, SEED_PRESETS, CONTRAST_LEVELS };

// Rolle → CSS-Name (kebab-case), Reihenfolge = Ausgabe.
export const ROLES = [
  'primary', 'onPrimary', 'primaryContainer', 'onPrimaryContainer', 'inversePrimary',
  'secondary', 'onSecondary', 'secondaryContainer', 'onSecondaryContainer',
  'tertiary', 'onTertiary', 'tertiaryContainer', 'onTertiaryContainer',
  'error', 'onError', 'errorContainer', 'onErrorContainer',
  'background', 'onBackground', 'surface', 'onSurface', 'surfaceVariant', 'onSurfaceVariant',
  'surfaceDim', 'surfaceBright', 'surfaceContainerLowest', 'surfaceContainerLow', 'surfaceContainer',
  'surfaceContainerHigh', 'surfaceContainerHighest', 'inverseSurface', 'inverseOnSurface',
  'outline', 'outlineVariant', 'shadow', 'scrim', 'surfaceTint',
  'primaryFixed', 'primaryFixedDim', 'onPrimaryFixed', 'onPrimaryFixedVariant',
  'secondaryFixed', 'secondaryFixedDim', 'onSecondaryFixed', 'onSecondaryFixedVariant',
  'tertiaryFixed', 'tertiaryFixedDim', 'onTertiaryFixed', 'onTertiaryFixedVariant',
];

export const kebab = (s) => s.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase());

function normHex(hex) {
  const h = String(hex || '').trim();
  if (!/^#?[0-9a-f]{6}$/i.test(h)) throw new Error('invalid_color');
  return (h[0] === '#' ? h : '#' + h).toUpperCase();
}

/** Schema-Objekt (DynamicScheme) für Leitfarbe/Modus/Kontrast. */
export function buildScheme(seedHex = DEFAULT_SEED, { dark = false, contrast = 0, tertiaryHex } = {}) {
  const seed = normHex(seedHex);
  const base = new SchemeTonalSpot(Hct.fromInt(argbFromHex(seed)), dark, contrast);
  const tert = tertiaryHex !== undefined ? tertiaryHex : (seed === DEFAULT_SEED ? DEFAULT_TERTIARY : null);
  if (!tert) return base;
  return new DynamicScheme({
    sourceColorArgb: base.sourceColorArgb,
    variant: base.variant,
    contrastLevel: contrast,
    isDark: dark,
    primaryPalette: base.primaryPalette,
    secondaryPalette: base.secondaryPalette,
    tertiaryPalette: TonalPalette.fromInt(argbFromHex(normHex(tert))),
    neutralPalette: base.neutralPalette,
    neutralVariantPalette: base.neutralVariantPalette,
  });
}

/** { role: '#RRGGBB' } für alle Rollen. */
export function schemeRoles(seedHex, opts) {
  const s = buildScheme(seedHex, opts);
  const out = {};
  for (const r of ROLES) {
    const dc = MaterialDynamicColors[r];
    if (!dc) continue;
    out[r] = hexFromArgb(dc.getArgb(s)).toUpperCase();
  }
  return out;
}

/** CSS-Deklarationen `--md-sys-color-*: #…;` (ein String, für :root oder [data-theme]). */
export function rolesToCss(roles) {
  return Object.entries(roles).map(([k, v]) => `--md-sys-color-${kebab(k)}: ${v};`).join(' ');
}

/** Relative Leuchtdichte + Kontrast nach WCAG 2.x. */
export function contrastRatio(hexA, hexB) {
  const lum = (hex) => {
    const n = parseInt(hex.slice(1), 16);
    const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
      v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  };
  const a = lum(hexA), b = lum(hexB);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** Paare Vordergrund/Hintergrund, die lesbar sein müssen (≥ 4.5:1). */
export const TEXT_PAIRS = [
  ['onPrimary', 'primary'], ['onPrimaryContainer', 'primaryContainer'],
  ['onSecondary', 'secondary'], ['onSecondaryContainer', 'secondaryContainer'],
  ['onTertiary', 'tertiary'], ['onTertiaryContainer', 'tertiaryContainer'],
  ['onError', 'error'], ['onErrorContainer', 'errorContainer'],
  ['onSurface', 'surface'], ['onSurfaceVariant', 'surface'],
  ['onSurface', 'surfaceContainerLowest'], ['onSurface', 'surfaceContainerHighest'],
  ['onSurfaceVariant', 'surfaceContainerHighest'], ['inverseOnSurface', 'inverseSurface'],
  ['primary', 'surface'], ['primary', 'surfaceContainerHigh'],
];
